import { Box, Typography } from '@mui/material';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';

import { useValidationExitGuard } from '../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../index';
import { buildValidationPieces } from '../../utils';
import useNotifyComment from './NotifyComment/useNotifyComment';
import NotifyMairoomComment from './NotifyComment';

export const FinishReturnToSender = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
}: ActionComponentProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const { values, changeValues } = useNotifyComment();

  const confirmAction = async () => {
    await updatePieces({
      pieces: buildValidationPieces(mailpieces, validationData),
      action: PieceAction.FinishReturnToSender,
      shouldNotifyRecipient: !values.skipNotifyRecipient,
    }).unwrap();

    const comment = values.comment.trim();

    if (comment.length) {
      await createMailpiecesListComment({
        text: comment,
        notifyMailroom: values.notifyMailroom,
        pieceIds: mailpieces.map((piece) => piece.id),
      }).unwrap();
    }

    onActionFinish();
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {mailpieces.length === 1 ? (
          <>Please return this item to its sender, and click Complete when you&apos;re done.</>
        ) : (
          <>
            Please return <strong>{mailpieces.length}</strong> items to their senders, and click Complete when
            you&apos;re done.
          </>
        )}
      </Typography>

      <NotifyMairoomComment values={values} onChange={changeValues} />
    </Box>
  );

  return (
    <>
      <DialogConfirmation
        open={actionStarted}
        loading={isLoading}
        maxWidth="sm"
        title={mailpieces.length === 1 ? 'Return item to sender' : 'Return selected items to senders'}
        content={content}
        acceptButtonHandleClick={confirmAction}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle="Complete"
        cancelButtonTitle="Cancel"
      />

      {exitConfirmDialog}
    </>
  );
};
